<template>
  <div class="customer-health mt-3 p-3" data-test="customer-health-panel">
    <div class="d-flex justify-content-between align-items-start flex-wrap mb-2" style="gap: 10px;">
      <div class="ch-intro">
        <h5 class="mb-1">Customer Health</h5>
        <p class="text-muted mb-0 ch-note">
          Which practices are using Teamio, which have gone quiet and which are still getting started.
          <strong>Nothing here sends a message or changes a practice's settings.</strong> The main signal is
          follow-ups; the other five features show whether a practice has adopted them but never raise an alert.
          <span v-if="hasRowActions" data-test="customer-health-email-note">
            <strong>Email</strong> opens the wording editor; saving there only stores the words, and a test goes only to your own address.
          </span>
        </p>
      </div>
      <b-button
        variant="outline-secondary"
        size="sm"
        class="text-nowrap"
        :disabled="loading"
        data-test="customer-health-refresh"
        @click="load(true)"
      >
        <i class="fa fa-refresh mr-1" :class="{ 'fa-spin': loading }"></i>Refresh
      </b-button>
    </div>

    <div v-if="loading && !data" class="text-muted py-4" data-test="customer-health-loading">
      <i class="fa fa-spinner fa-spin mr-1"></i>Checking every practice. The first check can take several seconds.
    </div>

    <b-alert v-if="error" show variant="danger" class="py-2 ch-note" data-test="customer-health-error">
      {{ error }}
      <b-button v-if="!data" size="sm" variant="link" class="p-0 ml-2" @click="load(false)">Try again</b-button>
    </b-alert>

    <template v-if="data">
      <b-alert v-if="data.partial" show variant="warning" class="py-2 ch-note" data-test="customer-health-partial">
        <strong>Some checks did not finish.</strong>
        {{ data.errors.length }} check{{ data.errors.length === 1 ? '' : 's' }} failed
        <span v-if="failedPractices.length">(<span v-for="(name, i) in failedPractices" :key="name">{{ i ? ', ' : '' }}{{ name }}</span>)</span>.
        A practice marked "Couldn't check" sits at the bottom and is left out of the totals. It is never shown as healthy or unused.
      </b-alert>

      <div class="ch-summary mb-2" data-test="customer-health-summary">
        <span v-for="c in chips" :key="c.key" class="ch-summary-item" :data-test="'customer-health-count-' + c.key">
          <b-badge :variant="c.variant" pill>{{ c.count }}</b-badge> {{ c.label }}
        </span>
        <span class="text-muted ch-note ml-1">of {{ data.summary.evaluated }} practices</span>
      </div>

      <p v-if="changes" class="ch-note ch-changes mb-2" data-test="customer-health-changes">{{ changes }}</p>

      <p class="ch-note text-muted mb-2" data-test="customer-health-adoption">
        Practices using each feature now:
        <span v-for="(a, i) in adoption" :key="a.key">{{ i ? ' · ' : '' }}{{ a.label }} {{ a.adopted }}</span>.
        <span v-if="data.summary.noAdoptedFeature">{{ data.summary.noAdoptedFeature }} use none of them.</span>
      </p>

      <div class="table-responsive">
        <table class="table table-sm ch-table mb-1">
          <thead>
            <tr>
              <th>Practice</th>
              <th>Status</th>
              <th class="ch-why-col">Why</th>
              <th>Features</th>
              <th v-if="hasRowActions" class="text-right" data-test="customer-health-actions-head"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.site.siteId" data-test="customer-health-row">
              <td class="align-middle ch-name">
                {{ row.site.siteName }}
                <span class="d-block text-muted ch-small">{{ row.since }}</span>
              </td>
              <td class="align-middle text-nowrap">
                <b-badge :variant="row.status.variant" data-test="customer-health-status">{{ row.status.label }}</b-badge>
                <span
                  v-if="row.change"
                  class="d-block ch-change"
                  :class="'ch-change--' + row.change.direction"
                  data-test="customer-health-change"
                  :data-direction="row.change.direction"
                >{{ row.change.text }}</span>
                <span
                  v-if="row.dots.length > 1"
                  class="d-block ch-history"
                  role="img"
                  :aria-label="row.historyLabel"
                  data-test="customer-health-history"
                >
                  <span
                    v-for="d in row.dots"
                    :key="d.day"
                    class="ch-dot"
                    :class="'ch-dot--' + d.key"
                    :title="d.title"
                    data-test="customer-health-history-dot"
                    :data-key="d.key"
                  ></span>
                </span>
              </td>
              <td class="align-middle ch-why-col">
                {{ row.status.reason }}
                <details v-if="row.checks.length" class="ch-checks" data-test="customer-health-checks">
                  <summary class="ch-checks-toggle" data-test="customer-health-checks-toggle">What we checked</summary>
                  <ol class="ch-check-list">
                    <li
                      v-for="c in row.checks"
                      :key="c.id"
                      class="ch-check"
                      :class="'ch-check--' + c.outcome"
                      :data-outcome="c.outcome"
                      data-test="customer-health-check"
                    >
                      <span class="ch-check-q">{{ c.n }}. {{ c.question }}</span>
                      <span class="ch-check-a">{{ c.answer }}</span>
                      <span v-if="c.outcome === 'decided'" class="ch-check-result" data-test="customer-health-check-result">Status set here: {{ row.status.label }}</span>
                    </li>
                  </ol>
                </details>
              </td>
              <td class="align-middle">
                <span
                  v-for="chip in row.chips"
                  :key="chip.key"
                  class="ch-feature"
                  :class="'ch-feature--' + chip.state"
                  :title="chip.title"
                  :data-test="'customer-health-feature-' + chip.key + '-' + chip.state"
                >{{ chip.label }}</span>
              </td>
              <td v-if="hasRowActions" class="align-middle text-right text-nowrap">
                <slot name="row-actions" :site="row.site" :status="row.status" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="ch-note text-muted mb-1" data-test="customer-health-legend">
        Features:
        <span class="ch-feature ch-feature--adopted">used recently</span>
        <span class="ch-feature ch-feature--dormant">used before, not recently</span>
        <span class="ch-feature ch-feature--never">never used</span>
        Hover a feature for the exact window and date.
      </p>

      <div class="ch-rules" data-test="customer-health-rules">
        <h6 class="ch-rules-title">How a status is decided</h6>
        <ol class="ch-rules-list">
          <li v-for="r in legend" :key="r.n" data-test="customer-health-rule">
            <strong>{{ r.n }}. {{ r.question }}</strong> {{ r.rule }}
            <span class="ch-rules-result">Result: {{ r.result }}.</span>
          </li>
        </ol>
        <p v-for="n in notes" :key="n.key" class="ch-note text-muted mb-1" :data-test="'customer-health-rule-note-' + n.key">{{ n.text }}</p>
      </div>

      <p class="ch-note text-muted mb-0" data-test="customer-health-footer">
        {{ cacheText }}. Rules {{ data.rules.version }}.
        <span v-if="historyText" data-test="customer-health-history-note">{{ historyText }}</span>
        <span v-if="data.excluded.length">Left out as test sites: {{ data.excluded.map((x) => x.label).join(', ') }}.</span>
      </p>
      <b-alert
        v-for="w in data.warnings"
        :key="w.siteId"
        show
        variant="warning"
        class="py-1 mt-2 mb-0 ch-note"
        data-test="customer-health-warning"
      >{{ w.siteName }}: {{ w.warning }}</b-alert>
    </template>
  </div>
</template>

<script>
import { fetchEngagementOverview, NO_TOKEN_MESSAGE } from '@/api/customerHealth';
import { extractApiError } from '@/utils/apiError';
import {
  describeStatus, explainChecks, ruleLegend, ruleNotes, summaryChips, featureChip, adoptionLine, cacheNote, formatDay,
  changeSince, historyDots, historyLabel, changesLine, historyNote,
} from '@/utils/customerHealth';

const LOAD_FAILED = "Couldn't load the customer health report. Please try again.";
const BAD_SHAPE = 'The customer health report came back in an unexpected shape. Please try again.';

export default {
  name: 'CustomerHealth',
  data() {
    return {
      loading: false,
      data: null,   // the server's overview, kept as returned
      error: null,
      seq: 0,       // bumped per request and on destroy, so a late answer never overwrites a newer one
    };
  },
  computed: {
    rows() {
      if (!this.data) return [];
      return this.data.sites.map((site) => {
        const dots = historyDots(site);
        return {
          site,
          status: describeStatus(site, this.data.rules),
          checks: explainChecks(site, this.data.rules),
          chips: (site.features || []).map(featureChip),
          since: site.createdDay ? `Since ${formatDay(site.createdDay)}` : 'Sign-up date not on record',
          change: changeSince(site),
          dots,
          historyLabel: historyLabel(dots),
        };
      });
    },
    changes() { return this.data ? changesLine(this.data.sites, this.data.history) : null; },
    historyText() { return this.data ? historyNote(this.data.history) : null; },
    chips() { return this.data ? summaryChips(this.data.sites) : []; },
    legend() { return this.data ? ruleLegend(this.data.rules) : []; },
    notes() { return this.data ? ruleNotes(this.data.rules) : []; },
    adoption() { return this.data ? adoptionLine(this.data.summary, this.data.sites) : []; },
    cacheText() { return this.data ? cacheNote(this.data.cache) : ''; },
    failedPractices() {
      const names = (this.data && this.data.errors ? this.data.errors : []).map((e) => e.siteName);
      return names.filter((n, i) => names.indexOf(n) === i);
    },
    // A later stage can add a per-row action by filling this slot. Nothing is drawn (no column) until then.
    hasRowActions() { return !!this.$scopedSlots['row-actions']; },
  },
  mounted() { this.load(false); },
  beforeDestroy() { this.seq += 1; },
  methods: {
    async load(refresh) {
      const mine = ++this.seq;
      this.loading = true;
      this.error = null;
      try {
        const auth = this.$store.state.getAuth ? this.$store.state.getAuth() : null;
        const data = await fetchEngagementOverview({ auth, refresh: refresh === true });
        if (mine !== this.seq) return;
        const ok = data && Array.isArray(data.sites) && Array.isArray(data.errors) && Array.isArray(data.excluded)
          && Array.isArray(data.warnings) && data.rules && data.summary;
        if (!ok) { this.error = BAD_SHAPE; return; }
        this.data = data;
      } catch (e) {
        if (mine !== this.seq) return;
        this.error = e && e.message === NO_TOKEN_MESSAGE ? NO_TOKEN_MESSAGE : extractApiError(e, LOAD_FAILED);
      } finally {
        if (mine === this.seq) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.customer-health { border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; }
.ch-intro { max-width: 720px; }
.ch-note { font-size: 0.85rem; }
.ch-small { font-size: 0.75rem; }
.ch-summary { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 18px; font-size: 0.9rem; }
.ch-summary-item { white-space: nowrap; }
.ch-table th { font-size: 0.75rem; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; }
.ch-table td { font-size: 0.88rem; }
.ch-name { font-weight: 600; }
.ch-changes { color: #334155; font-weight: 600; }
.ch-change { margin-top: 3px; font-size: 0.75rem; font-weight: 600; white-space: nowrap; }
.ch-change--worse { color: #b91c1c; }
.ch-change--better { color: #166534; }
.ch-change--moved { color: #475569; }
.ch-history { margin-top: 4px; white-space: nowrap; line-height: 1; }
.ch-dot { display: inline-block; width: 9px; height: 9px; margin-right: 3px; border-radius: 50%; background: #cbd5e1; cursor: default; }
.ch-dot--at_risk { background: #dc2626; }
.ch-dot--watch, .ch-dot--no_followups { background: #f59e0b; }
.ch-dot--onboarding { background: #38bdf8; }
.ch-dot--low_activity { background: #94a3b8; }
.ch-dot--healthy { background: #16a34a; }
.ch-dot--error { background: #1e293b; }
.ch-why-col { min-width: 260px; }
.ch-checks { margin-top: 4px; }
.ch-checks-toggle { font-size: 0.78rem; color: #475569; cursor: pointer; }
.ch-check-list { margin: 6px 0 2px; padding-left: 0; list-style: none; font-size: 0.8rem; }
.ch-check { margin-bottom: 6px; }
.ch-check-q { display: block; font-weight: 600; color: #334155; }
.ch-check-a { display: block; color: #475569; }
.ch-check-result { display: inline-block; margin-top: 2px; padding: 0 8px; border-radius: 10px; font-size: 0.75rem; font-weight: 600; background: #e0e7ff; color: #3730a3; }
.ch-check--skipped .ch-check-q, .ch-check--skipped .ch-check-a { color: #94a3b8; font-weight: 400; }
.ch-check--decided .ch-check-q { color: #1e293b; }
.ch-rules { margin: 14px 0 10px; padding: 10px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; }
.ch-rules-title { font-size: 0.85rem; font-weight: 700; margin-bottom: 6px; }
.ch-rules-list { margin: 0 0 8px; padding-left: 0; list-style: none; font-size: 0.82rem; color: #334155; }
.ch-rules-list li { margin-bottom: 4px; }
.ch-rules-result { color: #3730a3; font-weight: 600; }
.ch-feature { display: inline-block; margin: 0 4px 4px 0; padding: 1px 8px; border-radius: 10px; font-size: 0.75rem; border: 1px solid transparent; cursor: default; }
.ch-feature--adopted { background: #dcfce7; color: #166534; border-color: #bbf7d0; }
.ch-feature--dormant { background: #fef9c3; color: #854d0e; border-color: #fde68a; }
.ch-feature--never { background: #f1f5f9; color: #64748b; border-color: #e2e8f0; }
.ch-feature--error { background: #fee2e2; color: #991b1b; border-color: #fecaca; }
</style>
